'use client';

import { useSyncExternalStore } from 'react';

/**
 * Whether the viewer has asked for reduced motion.
 *
 * `useSyncExternalStore` rather than `useState` + an effect that seeds it: the
 * effect version has to call `setState` synchronously on mount to catch the
 * initial value, which is a cascading-render hazard (and a lint error in this
 * repo). This reads the query during render and subscribes for changes, with
 * a server snapshot of `false` so SSR and the first client render agree.
 *
 * Lived privately inside Eigenfield.tsx until the showcase's field sweep
 * needed the same answer. One copy, because two components disagreeing about
 * whether the user wants animation is exactly the bug this setting exists to
 * prevent.
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      const mq = reducedMotionQuery();
      mq?.addEventListener('change', onStoreChange);
      return () => mq?.removeEventListener('change', onStoreChange);
    },
    () => reducedMotionQuery()?.matches ?? false,
    () => false,
  );
}

/** The query, or null on a host with no `matchMedia` (jsdom; nothing a
 *  browser ships): no answer reads as no preference, never as a crash. The
 *  editor shell mounts this, so every suite that renders it would otherwise
 *  have to stub the API first (SocialProof.tsx reads it the same way). */
function reducedMotionQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
}
